/**
 * How large an uploaded symbol may be, in characters.
 *
 * Geotab caps a stored record itself, and this record also carries every other group's symbol, so
 * a single generous file would push the whole set over that cap and nothing could be saved at all.
 */
export const MAX_SYMBOL_SVG_CHARS = 40 * 1024;

/**
 * Things that must not appear in a symbol we are going to draw.
 *
 * The file comes from a person, but it is then shown to everybody, so it is treated as untrusted.
 * Script and event handlers are the obvious ones. `<foreignObject>` embeds arbitrary HTML.
 * `@import` and an `href` pointing anywhere but inside the file itself would fetch something at
 * draw time, which is both a way in and a symbol that changes without anyone editing it.
 */
const FORBIDDEN_PATTERNS: { pattern: RegExp; reason: string }[] = [
  { pattern: /<\s*script/i, reason: "script" },
  { pattern: /<\s*foreignObject/i, reason: "foreignObject" },
  { pattern: /<\s*iframe/i, reason: "iframe" },
  // `animate` alone would let `animateTransform` and `animateMotion` through.
  { pattern: /<\s*(image|animate\w*|set|handler)\b/i, reason: "external content" },
  { pattern: /\son\w+\s*=/i, reason: "event handler" },
  { pattern: /javascript:/i, reason: "javascript: url" },
  { pattern: /@import/i, reason: "@import" },
  // `fill:url(http://…)` in a <style> fetches on draw, which an attribute check misses.
  { pattern: /url\s*\(\s*["']?\s*(?!#)/i, reason: "external reference" },
  // An href is allowed only as a fragment inside the same file, e.g. href="#shape".
  { pattern: /(?:xlink:)?href\s*=\s*["'](?!#)/i, reason: "external reference" },
];

export type SymbolRejection =
  | { ok: true; svg: string }
  | { ok: false; reason: "empty" | "tooLarge" | "notSvg" | "unsafe" };

/**
 * Checks over an uploaded symbol and hands back the markup to store, or why it was refused.
 *
 * Nothing is rewritten beyond stripping the prologue: a symbol is drawn as its author drew it.
 * What is not allowed is refused outright rather than quietly cleaned, because a file that had to
 * be cleaned to be safe is a file whose author should hear about it.
 *
 * @param raw - The file's text.
 */
export function checkGroupSymbolSvg(raw: string): SymbolRejection {
  const withoutBom = raw.replace(/^﻿/, "").trim();

  if (withoutBom.length === 0) {
    return { ok: false, reason: "empty" };
  }
  if (withoutBom.length > MAX_SYMBOL_SVG_CHARS) {
    return { ok: false, reason: "tooLarge" };
  }

  const body = stripPrologue(withoutBom);

  if (!/^<svg[\s>]/i.test(body) || !/<\/svg>$/i.test(body)) {
    return { ok: false, reason: "notSvg" };
  }

  if (FORBIDDEN_PATTERNS.some(({ pattern }) => pattern.test(body))) {
    return { ok: false, reason: "unsafe" };
  }

  return { ok: true, svg: body };
}

/**
 * The symbol in one colour: the group's.
 *
 * Every fill and stroke in the markup is rewritten, both the ones written as attributes on the
 * shapes and the ones written as CSS in a `<style>` block. Both are needed: drawing programs
 * favour classes — CorelDRAW writes `.fil0 {fill:black}` — and a CSS rule beats a `fill`
 * attribute, so setting attributes alone would leave the drawing exactly as black as it was.
 *
 * `none` is left alone. It is how a shape says it is an outline or an invisible hit area, and
 * colouring those in would fill the gaps in the drawing.
 *
 * A two-colour drawing comes out of this in one colour. That is the point of it — the group's
 * colour is what tells the reader whose vehicle it is — and it is why this is worth saying out
 * loud rather than treating as a detail of loading a file.
 *
 * @param svg - Markup that has already been through {@link checkGroupSymbolSvg}.
 * @param color - The colour to paint it in, as `#rrggbb`.
 */
export function recolorGroupSymbolSvg(svg: string, color: string): string {
  return (
    svg
      // fill="…" and stroke="…" on a shape. The word boundary keeps `stroke-width` and
      // `fill-rule` out of it.
      .replace(/\b(fill|stroke)\s*=\s*"(?!none")[^"]*"/gi, (_match, property) => `${property}="${color}"`)
      .replace(/\b(fill|stroke)\s*=\s*'(?!none')[^']*'/gi, (_match, property) => `${property}='${color}'`)
      // fill: … and stroke: … inside a style block or a style attribute.
      .replace(/\b(fill|stroke)\s*:\s*(?!none)[^;}"']+/gi, (_match, property) => `${property}:${color}`)
  );
}

/**
 * Everything before the `<svg` tag, taken off.
 *
 * Drawing programs put a pile of things there. CorelDRAW writes an XML declaration, then a
 * doctype on its own line, then a comment naming itself; Illustrator and Inkscape write their own
 * variations. None of it is drawn, and a doctype can declare entities, so it all goes — in a loop
 * rather than in a fixed order, because the order differs between programs and the first version
 * of this only looked for two of them, each glued to the very start.
 */
function stripPrologue(markup: string): string {
  let rest = markup;
  let previousLength = -1;

  while (rest.length !== previousLength) {
    previousLength = rest.length;
    rest = rest
      .replace(/^\s+/, "")
      .replace(/^<\?[\s\S]*?\?>/, "")
      .replace(/^<!doctype[\s\S]*?>/i, "")
      .replace(/^<!--[\s\S]*?-->/, "");
  }

  return rest.trim();
}

/**
 * The symbol as a URL deck.gl can load as an image.
 *
 * `encodeURIComponent` rather than base64 so the markup stays readable in a debugger, and because
 * a data URL is what an `IconLayer` wants either way.
 */
export function groupSymbolDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * How many recoloured symbols to hold on to.
 *
 * A colour input reports a new colour on every movement of the pointer, so a drag through the
 * picker asks for dozens that will never be asked for again. A fleet drawn in a handful of
 * colours never reaches this.
 */
const MAX_CACHED_RECOLOURS = 64;

const recolourCache = new Map<string, string>();

/**
 * {@link recolorGroupSymbolSvg}, remembered.
 *
 * The map draws one symbol per vehicle and the list draws one per row, so the same markup in the
 * same colour is asked for once per vehicle — three regular expressions over up to forty
 * kilobytes each time, on every poll. Only the distinct pairs are worked out.
 */
export function recolorGroupSymbolSvgCached(svg: string, color: string): string {
  const key = `${color}::${svg}`;
  const cached = recolourCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const recoloured = recolorGroupSymbolSvg(svg, color);
  recolourCache.set(key, recoloured);

  for (const oldest of recolourCache.keys()) {
    if (recolourCache.size <= MAX_CACHED_RECOLOURS) break;
    recolourCache.delete(oldest);
  }

  return recoloured;
}
